import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Chip,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  PlayArrow,
  History,
  Devices,
  Add,
  Settings,
  PlaylistPlay,
  Dashboard as DashboardIcon,
  Storage,
  AccountCircle,
  Queue,
  SettingsApplications,
  Loop,
  Menu as MenuIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitch from './LanguageSwitch';
import { UserMenu } from './UserMenu';
import { usePermission } from '../hooks/usePermission';

// 导航项配置
interface NavItem {
  path: string;
  labelKey: string;
  icon: React.ReactNode;
  permission?: string;
  variant?: 'text' | 'outlined';
}

const Header: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const theme = useTheme();
  
  // 响应式断点
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  
  // 抽屉菜单状态
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 导航项配置
  const navItems: NavItem[] = [
    { path: '/', labelKey: 'header.dashboard', icon: <DashboardIcon />, permission: undefined },
    { path: '/devices', labelKey: 'header.deviceManagement', icon: <Devices />, permission: 'MANAGE_CONFIG' },
    { path: '/register-device', labelKey: 'header.registerDevice', icon: <Add />, permission: 'MANAGE_CONFIG', variant: 'outlined' },
    { path: '/apk-versions', labelKey: 'header.apkVersions', icon: <Settings />, permission: 'UPLOAD_APK' },
    { path: '/account-manager', labelKey: 'header.accountManager', icon: <AccountCircle />, permission: 'MANAGE_CONFIG' },
    { path: '/post-release-testing', labelKey: 'header.postReleaseTesting', icon: <PlaylistPlay />, permission: 'VIEW_REPORTS' },
    { path: '/sql-validation', labelKey: 'header.sqlValidation', icon: <Storage />, permission: 'VIEW_REPORTS' },
    { path: '/queue-management', labelKey: 'queueManagement.title', icon: <Queue />, permission: 'MANAGE_CONFIG' },
    { path: '/cash-only', labelKey: 'header.cashOnlyControl', icon: <Loop />, permission: 'MANAGE_CONFIG' },
    { path: '/cash-only-device-pool', labelKey: 'header.cashOnlyDevicePool', icon: <Devices />, permission: 'MANAGE_CONFIG' },
    { path: '/test-configs', labelKey: 'testConfigManagement.title', icon: <SettingsApplications />, permission: 'MANAGE_CONFIG' },
    { path: '/apk-test', labelKey: 'header.apkTest', icon: <Settings />, permission: 'START_TEST' },
  ];

  // 过滤有权限的导航项
  const visibleNavItems = navItems.filter(
    item => !item.permission || hasPermission(item.permission as any)
  );

  // 检查当前路径是否选中
  const isActive = (path: string) => {
    if (path === '/') {
      return location.pathname === '/' || location.pathname === '';
    }
    return location.pathname.startsWith(path);
  };

  // 处理导航
  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  // 渲染导航按钮（桌面端）
  const renderNavButton = (item: NavItem) => (
    <Button
      key={item.path}
      color="inherit"
      startIcon={item.icon}
      onClick={() => handleNavigate(item.path)}
      variant={item.variant}
      sx={{
        fontWeight: isActive(item.path) ? 'bold' : 'normal',
        backgroundColor: isActive(item.path) ? 'rgba(255,255,255,0.15)' : 'transparent',
        borderColor: item.variant === 'outlined' ? 'white' : undefined,
        whiteSpace: 'nowrap',
        minWidth: 'auto',
        px: isTablet ? 1 : 2,
        fontSize: isTablet ? '0.75rem' : '0.875rem',
        '&:hover': {
          backgroundColor: 'rgba(255,255,255,0.25)',
          borderColor: item.variant === 'outlined' ? 'white' : undefined,
        },
      }}
    >
      {isTablet ? null : t(item.labelKey)}
    </Button>
  );

  // 渲染抽屉菜单项（移动端）
  const renderDrawerItem = (item: NavItem) => (
    <ListItem key={item.path} disablePadding>
      <ListItemButton
        onClick={() => handleNavigate(item.path)}
        selected={isActive(item.path)}
        sx={{
          '&.Mui-selected': {
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            '& .MuiListItemIcon-root': {
              color: theme.palette.primary.contrastText,
            },
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 40 }}>
          {item.icon}
        </ListItemIcon>
        <ListItemText primary={t(item.labelKey)} />
      </ListItemButton>
    </ListItem>
  );

  return (
    <>
      <AppBar position="sticky" elevation={2}>
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }}>
          {/* 移动端汉堡菜单 */}
          {isMobile && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo/标题 */}
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: 'bold',
              flexShrink: 0,
              cursor: 'pointer',
              fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
            }}
            onClick={() => handleNavigate('/')}
          >
            {isMobile ? 'KAP' : t('header.title')}
          </Typography>

          {/* 桌面端导航 - 可滚动 */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { md: 0.5, lg: 1 },
                ml: 2,
                flex: 1,
                overflowX: 'auto',
                overflowY: 'hidden',
                scrollbarWidth: 'none', // Firefox
                '&::-webkit-scrollbar': { display: 'none' }, // Chrome
                // 滚动时的渐变效果
                maskImage: 'linear-gradient(to right, black 95%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to right, black 95%, transparent 100%)',
              }}
            >
              <Chip
                label={t('header.location')}
                color="primary"
                size="small"
                variant="outlined"
                sx={{ 
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  flexShrink: 0,
                }}
              />
              {visibleNavItems.map(renderNavButton)}
            </Box>
          )}

          {/* 右侧固定区域 */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.5, sm: 1 },
              ml: 'auto',
              flexShrink: 0,
            }}
          >
            <LanguageSwitch />
            <UserMenu />
          </Box>
        </Toolbar>
      </AppBar>

      {/* 移动端抽屉菜单 */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            maxWidth: '80vw',
          },
        }}
      >
        {/* 抽屉头部 */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            backgroundColor: theme.palette.primary.main,
            color: 'white',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
            {t('header.title')}
          </Typography>
          <IconButton color="inherit" onClick={() => setDrawerOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 位置标签 */}
        <Box sx={{ p: 2 }}>
          <Chip
            label={t('header.location')}
            color="primary"
            size="small"
            sx={{ width: '100%' }}
          />
        </Box>

        <Divider />

        {/* 导航列表 */}
        <List sx={{ flex: 1, overflowY: 'auto' }}>
          {visibleNavItems.map(renderDrawerItem)}
        </List>

        <Divider />

        {/* 底部区域 */}
        <Box sx={{ p: 2 }}>
          <Typography variant="caption" color="text.secondary">
            © 2025 Kiosk Automation Platform
          </Typography>
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
